import React from 'react';
import { motion } from 'framer-motion';

interface FeatureListProps {
  features: string[];
  className?: string;
  animated?: boolean;
  staggerDelay?: number;
  variant?: 'default' | 'compact' | 'grid';
}

export const FeatureList: React.FC<FeatureListProps> = ({ 
  features, 
  className = "", 
  animated = true,
  staggerDelay = 0.08,
  variant = 'default'
}) => {
  const containerClasses = variant === 'grid' 
    ? `grid grid-cols-1 md:grid-cols-2 gap-3 ${className}`
    : `space-y-3 ${className}`;

  return (
    <div className={containerClasses}>
      {features.map((feature, index) => (
        <motion.div
          key={index}
          initial={animated ? { opacity: 0, y: 20 } : false}
          animate={animated ? { opacity: 1, y: 0 } : false}
          transition={animated ? { 
            delay: index * staggerDelay, 
            duration: 0.4,
            ease: [0.25, 0.25, 0, 1]
          } : undefined}
          whileHover={animated ? { 
            scale: 1.03,
            y: -2,
            transition: { duration: 0.2, ease: "easeOut" }
          } : undefined}
          className="group flex items-center gap-4 p-4 bg-gradient-to-r from-white/[0.04] to-white/[0.01] rounded-xl border border-white/10 hover:border-primary-400/50 hover:bg-gradient-to-r hover:from-primary-500/10 hover:to-primary-600/5 hover:shadow-lg hover:shadow-primary-500/15 transition-all duration-300 cursor-pointer backdrop-blur-sm"
          style={{
            willChange: 'transform, opacity',
            transform: 'translateZ(0)',
          }}
        >
          {/* Enhanced Icon-style bullet point */}
          <div className="flex-shrink-0 relative">
            {/* Outer glow ring */}
            <div className="absolute inset-0 w-10 h-10 bg-gradient-to-r from-primary-400/30 to-accent-500/30 rounded-lg blur-sm opacity-60 group-hover:opacity-100 group-hover:blur-md transition-all duration-300"></div>
            
            {/* Main icon container */}
            <div className="relative w-10 h-10 bg-gradient-to-br from-primary-400/20 via-primary-500/30 to-primary-600/20 rounded-lg border border-primary-400/30 group-hover:border-primary-300/50 flex items-center justify-center shadow-lg shadow-primary-500/20 group-hover:shadow-primary-400/30 transition-all duration-300 backdrop-blur-sm">
              
              {/* Dynamic icon based on index */}
              {index % 5 === 0 && (
                // Gear/Settings icon
                <svg className="w-5 h-5 text-primary-300 group-hover:text-primary-200 transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              )}
              {index % 5 === 1 && (
                // Lightning/Power icon
                <svg className="w-5 h-5 text-primary-300 group-hover:text-primary-200 transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              )}
              {index % 5 === 2 && (
                // Shield/Security icon
                <svg className="w-5 h-5 text-primary-300 group-hover:text-primary-200 transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {index % 5 === 3 && (
                // Rocket/Launch icon
                <svg className="w-5 h-5 text-primary-300 group-hover:text-primary-200 transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.906.114L9.846 13.4 6.5 11.866a1 1 0 010-1.732L9.846 8.6l1.215-4.56A1 1 0 0112 2z" clipRule="evenodd" />
                </svg>
              )}
              {index % 5 === 4 && (
                // Clock/Time icon
                <svg className="w-5 h-5 text-primary-300 group-hover:text-primary-200 transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              )}
              
              {/* Subtle rotating accent - slower animation */}
              <div className="absolute inset-0 rounded-lg opacity-20 bg-gradient-conic from-primary-400/40 via-transparent to-primary-400/40 group-hover:animate-spin-slow transition-all duration-500"></div>
            </div>
          </div>
          
          <span className="text-gray-200 text-sm leading-relaxed group-hover:text-gray-100 transition-colors duration-300 flex-1">
            {feature}
          </span>
        </motion.div>
      ))}
    </div>
  );
};
